import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { CustomErrorCodes } from '../../shared/errors/custom-error/custom-error-codes';
import { CORE_CONFIGURATION } from '../configuration';
import { HEADER_API, HEADER_AWS_API } from '../constants';
import { ApiModule } from './api.module';
import {
  ActivationProductErrorResponse,
  CustomErrorPageResponse,
  CustomErrorResponse,
  DetailProductErrorResponse,
  MaintenanceErrorResponse,
  SessionExpiredErrorResponse
} from './custom-error-response.type';
import { CustomErrorService } from './custom-error.service';
import { BridgeService } from '../bridge';
import { environment } from 'src/environments/environment';
import { datadogConfigLogger } from 'src/datadog.config';
import { datadogLogs } from '@datadog/browser-logs';

@Injectable({
  providedIn: ApiModule,
  deps: [HttpClient, CustomErrorService, CORE_CONFIGURATION]
})
export class BaseApiService {
  private _header: {[p: string]: string | string[]} = {
    [HEADER_API]: 'true'
  };

  private _initial_header = {...this._header};

  constructor(
    private http: HttpClient,
    private customErrorService: CustomErrorService,
    private store: Store,
    public bridge: BridgeService,
    @Inject(CORE_CONFIGURATION) private config
  ) {
  }

  private resolveAppConfig(apiHost, apiBasePath): {apiHostResolved: string, apiBasePathResolved: string} {
    const {aem: {config}, session: {device}} = this.store.snapshot();

    const envAppVers = config?.environments[device?.appVersion];

    const apiHostResolved = envAppVers?.apiHost || apiHost;
    const apiBasePathResolved = apiBasePath && envAppVers?.journeyApiBasePath || apiBasePath;

    return {
      apiHostResolved,
      apiBasePathResolved: apiBasePathResolved ? `/${apiBasePathResolved}` : ''
    };
  }
  
  private resolveCustomHost(host: string): string {
    if (!host) {
      return '';
    }
    const {aem: {config}, session: {device}} = this.store.snapshot();
    const envAppVers = config?.environments[device?.appVersion];
    return envAppVers?.processFlowApiHost || host;
  }

  private resolveApiAWSLocal(baseurl: string) {
    baseurl = ( baseurl
      .replace('https://dxl.vodafone.it/', '')
      .replace('api-biz-bs', '')
      .replace('api-biz', '')
      .replace(environment.apiAWS, '') );

    return baseurl;
  }

  private formatErrors(error: any) {
    return throwError(error);
  }

  get headers(): {[p: string]: string | string[]} {
    return this._header;
  }

  header(key: string, value: string | string[]) {
    this._header[key] = value;

    return this;
  }

  /* eslint-disable */
  get(getParams: {
    apiBasePath?: string
    host?: string;
    path: string;
    params?: HttpParams;
    hideError?: boolean;
    responseType?: 'arrayBuffer' | 'blob' | 'text' | 'json';
    awsApi?: boolean;
  }): Observable<any> {
    const {apiBasePath = '', host = '', params = new HttpParams(), hideError = false, responseType, awsApi} = getParams;

    const apiHost = this.config.apiHost;
    let urlPath = getParams?.path;
    
    if( awsApi ){ // if version
      this._header = {...this._initial_header, [HEADER_AWS_API]: 'true'};
      if(!this.bridge.isWebView()) { urlPath = this.resolveApiAWSLocal(getParams?.path); };
      // apiHost = this.config.apiHostAWS;
    }
    
    const {apiHostResolved, apiBasePathResolved} = this.resolveAppConfig(awsApi ? '' : apiHost, awsApi ? '' : apiBasePath);
    const customHost = this.resolveCustomHost(host);

    return this.http
      .get(`${customHost || apiHostResolved}${apiBasePathResolved}${urlPath}`, {
        params,
        reportProgress: true,
        headers: new HttpHeaders(this.headers),
        ...(({responseType} as any))
      })
      .pipe(
        catchError(this.logOnDatadog),
        this.catchCustomError(),
        catchError((error: HttpErrorResponse) => {
          return !hideError ? this.formatErrors(error) : of(null);
        }),
        tap(() => {
          !awsApi? (this._header = {...this._initial_header}) : (this._header = {...this._initial_header, [HEADER_AWS_API]: 'true'})
        })
      );
  }

  post(getParams: {
    globalErrorHandler?: boolean;
    host?: string;
    apiBasePath?: string;
    path: string;
    body?: any;
    hideError?: boolean;
    awsApi?: boolean;
  }): Observable<any> {
    const {apiBasePath = '', body = {}, host = '', globalErrorHandler = true, hideError = false, awsApi} = getParams;
    
    const apiHost = this.config.apiHost;
    let urlPath = getParams?.path;
    
    if( awsApi ){ 
      this._header = {...this._initial_header, [HEADER_AWS_API]: 'true'};
      if(!this.bridge.isWebView()) { urlPath = this.resolveApiAWSLocal(getParams?.path); };
    }
    const {apiHostResolved, apiBasePathResolved} = this.resolveAppConfig(awsApi ? '' : apiHost, awsApi ? '' : apiBasePath);
    const customHost = this.resolveCustomHost(host);

    return this.http
      .post(`${customHost || apiHostResolved}${apiBasePathResolved}${urlPath}`, body, {
        reportProgress: true,
        headers: new HttpHeaders(this.headers)
      })
      .pipe(
        catchError(this.logOnDatadog),
        this.catchCustomError(globalErrorHandler),
        catchError((error) => {
          return !hideError ? this.formatErrors(error) : of(null);
        }),
        tap(() => {
          !awsApi? (this._header = {...this._initial_header}) : (this._header = {...this._initial_header, [HEADER_AWS_API]: 'true'})
        })
      );
  }

  put(getParams: {
    globalErrorHandler?: boolean;
    host?: string;
    apiBasePath?: string
    path: string;
    body?: any;
    hideError?: boolean;
    awsApi?: boolean;
  }): Observable<any> {
    const {apiBasePath = '', path, body = {},awsApi,hideError,globalErrorHandler = true,host = ''} = getParams;

    const apiHost = this.config.apiHost;
    let urlPath = getParams?.path;

    if( awsApi ){ 
      this._header = {...this._initial_header, [HEADER_AWS_API]: 'true'};
      if(!this.bridge.isWebView()) { urlPath = this.resolveApiAWSLocal(getParams?.path); };
    }

    const {apiHostResolved, apiBasePathResolved} = this.resolveAppConfig(awsApi ? '' : apiHost, awsApi ? '' : apiBasePath);
    const customHost = this.resolveCustomHost(host);
  
    return this.http
      .put(`${customHost || apiHostResolved}${apiBasePathResolved}${path}`, body, {
        reportProgress: true,
        headers: new HttpHeaders(this.headers)
      })
      .pipe(
        catchError(this.logOnDatadog),
        this.catchCustomError(globalErrorHandler),
        catchError((error) => {
          return !hideError ? this.formatErrors(error) : of(null);
        }),
        tap(() =>  !awsApi? (this._header = {...this._initial_header}) : (this._header = {...this._initial_header, [HEADER_AWS_API]: 'true'}))
      );
  }

  patch(getParams: {
    globalErrorHandler?: boolean;
    host?: string;
    apiBasePath?: string;
    path: string;
    body?: any;
  }): Observable<any> {
    const {apiBasePath = '', host = '', path, body = {}, globalErrorHandler = true} = getParams;

    const {apiHostResolved, apiBasePathResolved} = this.resolveAppConfig(this.config.apiHost, apiBasePath);
    const customHost = this.resolveCustomHost(host);

    return this.http
      .patch(`${customHost || apiHostResolved}${apiBasePathResolved}${path}`, body, {
        reportProgress: true,
        headers: new HttpHeaders(this.headers)
      })
      .pipe(
        catchError(this.logOnDatadog),
        this.catchCustomError(globalErrorHandler),
        catchError(this.formatErrors),
        tap(() => (this._header = {...this._initial_header}))
      );
  }

  delete(getParams: {
    apiBasePath?: string
    host?: string;
    path: string;
  }): Observable<any> {
    const {apiBasePath = '', host = '', path} = getParams;

    const {apiHostResolved, apiBasePathResolved} = this.resolveAppConfig(this.config.apiHost, apiBasePath);
    const customHost = this.resolveCustomHost(host);

    return this.http
      .delete(`${customHost || apiHostResolved}${apiBasePathResolved}${path}`, {
        reportProgress: true,
        headers: new HttpHeaders(this.headers)
      })
      .pipe(
        catchError(this.logOnDatadog),
        this.catchCustomError(),
        catchError(this.formatErrors),
        tap(() => (this._header = {...this._initial_header}))
      );
  }

  logOnDatadog(error: HttpErrorResponse): Observable<HttpErrorResponse> {
    datadogLogs.logger.error(`${error.type} ${error.status} API failure on ${error.url}: ${error.message}`,
    {
      trace_id: error.headers.get('trace_id'),
      errorData: error
    });
    return throwError(error);
  }

  catchCustomError(globalHandler = true) {
    const parser = this.customErrorService.parse;

    return (source: Observable<any>) =>
      new Observable((observer) => {
        return source.subscribe({
          next(response) {
              observer.next(response);
          },
          error(e) {
            if (e.status === 502 ) {
              observer.error(new CustomErrorResponse(
                parser({code:e.status,message: 'Al momento il servizio non è disponibile',globalHandler})
              ))
            }
            if (e.error?.code === 'activation:rest-client-response-error') {
              observer.error(new ActivationProductErrorResponse(
                parser({code:e.status,message: 'Abbiamo riscontrato un errore imprevisto. Per favore riprova più tardi',globalHandler})
              ))
            }
            if (e.error?.code === 'catalog:product-not-found') {
              observer.error(new DetailProductErrorResponse(
                parser({code:e.status,message: 'Al momento il prodotto non è disponibile',globalHandler})
              ))
            }
            if (e.status === 404 && e.error?.code?.includes('news:push-notification-preference-error')) {
              observer.error(e)
            }
            if (e.error?.code === 411) {
              observer.error(
                new MaintenanceErrorResponse(
                  parser({code: e.error?.code, message: e.error?.message, globalHandler})
                ));
            } else if (e.error?.code === 401 ||
              e.error?.error?.toLowerCase() === 'expired jwt' ||
              e.error?.message?.toLowerCase() === 'expired jwt') {
              observer.error(
                new SessionExpiredErrorResponse(
                  parser({code: e.error?.code, globalHandler})
                ));
            } else if (e.error?.code in CustomErrorCodes) {
              observer.error(
                new CustomErrorPageResponse(
                  parser({code: e.error?.code, globalHandler})
                ));
            } else {
              observer.error(
                new CustomErrorResponse(
                  parser({code: e.error?.code || e.status, message: e.error?.error_description || e.error?.message, globalHandler})
                ));
            }
          },
          complete() {
            observer.complete();
          }
        });
      });
  }
}
